import { useEffect, useRef, useState } from 'react'
import { storedServer } from '../api/client'
import {
  createDroneFeed,
  deleteDroneFeed,
  droneFeeds,
  rotateDroneFeedKey,
  watchDroneFeed,
} from '../api/endpoints'
import type { DroneFeed } from '../api/types'
import { absoluteWhepUrl, djiFlyAddress, playWhep, WhepError } from '../video/whep'
import { CloseIcon } from './FeatureEditor'
import { Modal } from './Modal'

/** How often the list asks whether each feed is on the air. */
const POLL_MS = 5000

/**
 * The event's drone feeds: which are streaming, the one to watch, and how to set a drone up.
 *
 * Hosts only, like the server's rule: a drone over the field sees both sides. Polled rather than
 * pushed -- whether a feed is live is a fact the media server holds, and a few seconds' lag on a
 * dot going green is nothing next to a video that is itself live.
 */
export function DroneFeedsSection({
  missionId,
  watchingId,
  onWatch,
}: {
  missionId: string
  watchingId: string | null
  onWatch: (feed: DroneFeed) => void
}) {
  const [feeds, setFeeds] = useState<DroneFeed[]>([])
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [settingUp, setSettingUp] = useState<DroneFeed | null>(null)

  async function refresh() {
    const result = await droneFeeds(missionId)
    if (result.ok) {
      setFeeds(result.value)
      setError(null)
    } else {
      setError(result.error.message)
    }
  }

  useEffect(() => {
    void refresh()
    const timer = setInterval(() => void refresh(), POLL_MS)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId])

  return (
    <>
      <div className="panel-head">
        <h3>
          Drones <span className="badge">{feeds.filter((feed) => feed.live).length} live</span>
        </h3>
        <button className="secondary" style={{ height: 28 }} onClick={() => setAdding(true)}>
          Add
        </button>
      </div>

      {error && <p className="banner warning" style={{ margin: 0 }}>{error}</p>}

      {feeds.length === 0 && !error && (
        <p className="tertiary" style={{ margin: 0 }}>
          Add a drone to watch its camera here. Its pilot streams to it from DJI Fly.
        </p>
      )}

      {feeds.map((feed) => (
        <div key={feed.id} className="person-row" data-testid={`drone-${feed.id}`}>
          <div className="who">
            <span className="name">{feed.name}</span>
          </div>
          <span className={`fix ${feed.live ? 'live' : 'none'}`}>{feed.live ? 'live' : 'off air'}</span>
          <div className="row" style={{ gap: 6 }}>
            <button
              style={{ height: 28 }}
              disabled={!feed.live || feed.id === watchingId}
              title={feed.live ? undefined : 'Nothing is streaming to it yet'}
              onClick={() => onWatch(feed)}
            >
              Watch
            </button>
            <button className="secondary" style={{ height: 28 }} onClick={() => setSettingUp(feed)}>
              Set up
            </button>
          </div>
        </div>
      ))}

      {adding && (
        <AddDroneModal
          onCancel={() => setAdding(false)}
          onAdd={async (name) => {
            const result = await createDroneFeed(missionId, name)
            if (!result.ok) return result.error.message
            setAdding(false)
            await refresh()
            setSettingUp(result.value)
            return null
          }}
        />
      )}

      {settingUp && (
        <SetUpDroneModal
          feed={feeds.find((feed) => feed.id === settingUp.id) ?? settingUp}
          onClose={() => setSettingUp(null)}
          onRotate={async () => {
            const result = await rotateDroneFeedKey(missionId, settingUp.id)
            if (result.ok) setSettingUp(result.value)
            await refresh()
          }}
          onDelete={async () => {
            await deleteDroneFeed(missionId, settingUp.id)
            setSettingUp(null)
            await refresh()
          }}
        />
      )}
    </>
  )
}

function AddDroneModal({
  onCancel,
  onAdd,
}: {
  onCancel: () => void
  onAdd: (name: string) => Promise<string | null>
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal title="Add a drone" onClose={onCancel}>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault()
          setBusy(true)
          setError(await onAdd(name.trim()))
          setBusy(false)
        }}
      >
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="drone-name">Name</label>
          <input
            id="drone-name"
            autoFocus
            maxLength={40}
            value={name}
            placeholder="Mini 4 Pro"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        {error && <p className="banner danger" style={{ margin: 0 }}>{error}</p>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={busy || name.trim().length === 0}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * How to point DJI Fly at a feed, with the address to type in.
 *
 * Spelled out step by step because it is done once, on a remote controller's small screen, by
 * somebody who has not done it before -- and DJI Fly offers no way to paste from this page.
 */
function SetUpDroneModal({
  feed,
  onClose,
  onRotate,
  onDelete,
}: {
  feed: DroneFeed
  onClose: () => void
  onRotate: () => Promise<void>
  onDelete: () => Promise<void>
}) {
  const address = djiFlyAddress(feed.publishUrl, feed.streamKey)
  const [copied, setCopied] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // The address is on screen to read; a refused clipboard is not worth an error.
    }
  }

  return (
    <Modal title={`Set up ${feed.name}`} onClose={onClose} wide>
      <div className="stack">
        <div>
          <div className="eyebrow">Address for DJI Fly</div>
          <div className="spread" style={{ gap: 8, marginTop: 4 }}>
            <code className="mono" style={{ wordBreak: 'break-all', color: 'var(--accent)' }}>
              {address}
            </code>
            <button className="secondary" style={{ height: 28, flex: '0 0 auto' }} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="tertiary" style={{ margin: '6px 0 0' }}>
            It carries this drone&apos;s stream key. Anyone with it can put video on this feed, so keep
            it to the pilot.
          </p>
        </div>

        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6 }}>
          <li>Connect the remote to the internet: Wi-Fi, or a phone&apos;s hotspot at the field.</li>
          <li>
            In DJI Fly, open the camera view, then <strong>••• → Transmission → Live Streaming
            Platforms → RTMP</strong>.
          </li>
          <li>Enter the address above, choose the quality, and start the stream.</li>
          <li>The dot here turns green within a few seconds; then press Watch.</li>
        </ol>
        <p className="tertiary" style={{ margin: 0 }}>
          The DJI RC 2 streams at up to 720p, and recent DJI Fly versions ask for a microphone
          plugged into the remote before they will start. The original DJI RC cannot stream.
        </p>

        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button className="danger-outline" onClick={() => setConfirmDelete(true)}>
            Remove drone
          </button>
          <div className="row" style={{ gap: 8 }}>
            <button className="secondary" onClick={() => void onRotate()}>
              New key
            </button>
            <button onClick={onClose}>Done</button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <Modal title={`Remove ${feed.name}?`} onClose={() => setConfirmDelete(false)}>
          <div className="stack">
            <p style={{ margin: 0 }}>
              Its address stops working, for the pilot and for anyone watching. Add the drone again to
              stream from it later.
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button className="danger" onClick={() => void onDelete()}>
                Remove
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  )
}

/**
 * One feed, playing, over the map.
 *
 * A fresh pass is asked for on every open: passes last minutes, a TOC watches for hours, and an
 * open stream is not cut when its pass expires -- only reopening needs a new one.
 */
export function DroneVideoPanel({
  missionId,
  feed,
  onClose,
}: {
  missionId: string
  feed: DroneFeed
  onClose: () => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const [state, setState] = useState<'connecting' | 'playing' | 'lost' | 'failed'>('connecting')
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let close: (() => void) | null = null
    let cancelled = false

    void (async () => {
      const pass = await watchDroneFeed(missionId, feed.id)
      if (cancelled) return
      if (!pass.ok) {
        setState('failed')
        setError(pass.error.message)
        return
      }
      try {
        const element = video.current
        if (!element) return
        close = await playWhep(absoluteWhepUrl(pass.value.whepUrl, storedServer()), element, (next) => {
          if (next === 'connected') setState('playing')
          if (next === 'disconnected' || next === 'failed') setState('lost')
        })
        if (cancelled) close()
      } catch (cause) {
        if (cancelled) return
        setState('failed')
        setError(cause instanceof WhepError ? cause.message : 'The feed could not be opened.')
      }
    })()

    return () => {
      cancelled = true
      close?.()
    }
  }, [missionId, feed.id, attempt])

  return (
    <div className="video-panel" role="dialog" aria-label={`${feed.name} camera`}>
      <div className="spread" style={{ marginBottom: 8 }}>
        <div className="row" style={{ gap: 8 }}>
          <strong>{feed.name}</strong>
          <span className={`fix ${state === 'playing' ? 'live' : state === 'connecting' ? 'stale' : 'none'}`}>
            {state === 'playing' ? 'live' : state === 'connecting' ? 'connecting' : 'no picture'}
          </span>
        </div>
        <div className="row" style={{ gap: 4 }}>
          <button
            className="icon-button"
            aria-label="Full screen"
            title="Full screen"
            onClick={() => void video.current?.requestFullscreen?.()}
          >
            ⤢
          </button>
          <button className="icon-button" aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
      </div>

      <div className="video-frame">
        <video ref={video} autoPlay muted playsInline />
        {state !== 'playing' && (
          <div className="video-overlay">
            {state === 'connecting' && <span>Opening the feed…</span>}
            {(state === 'failed' || state === 'lost') && (
              <div className="stack" style={{ alignItems: 'center', gap: 8 }}>
                <span>{error ?? 'The picture was lost. The drone may be out of range.'}</span>
                <button
                  className="secondary"
                  onClick={() => {
                    setState('connecting')
                    setError(null)
                    setAttempt((n) => n + 1)
                  }}
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
